import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  UserRound,
  X,
  Check,
  CheckCircle2,
  ExternalLink,
  FileSignature,
  Loader2,
  Copy,
  ChevronDown,
  Info,
  Landmark,
  Lock,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { CARD, agoLabel } from '../../onboardingSupport/leadUtils';
import VaGovCheck, { VA_GOV_LABEL } from '../../onboardingSupport/VaGovCheck';
import {
  confirmIntakeFacts,
  sendIntakeServiceAgreement,
  voidIntakeAgreement,
  fetchIntakeSignLink,
  beginIntakeCorrection,
  cancelIntakeCorrection,
} from '../../../../services/clientIntake';

// The signup's facts + agreement cards, shared by the Signups page and the
// board's signup flyover (Nic 2026-09-22: one component in two places, so
// they can never drift). Each section talks to its backend through an `api`
// object: the Signups page passes intakeApiFor(userId) (/api/intake/*), the
// board passes its lead-scoped twin (/api/onboarding-board/leads/:id/signup/*).

// Signups page backend for one pending client.
export const intakeApiFor = (userId) => ({
  confirmFacts: (facts) => confirmIntakeFacts(userId, facts),
  sendAgreement: (opts) => sendIntakeServiceAgreement(userId, opts),
  voidAgreement: () => voidIntakeAgreement(userId),
  signLink: () => fetchIntakeSignLink(userId),
  beginCorrection: (why) => beginIntakeCorrection(userId, why),
  cancelCorrection: () => cancelIntakeCorrection(userId),
});

// 403s are already toasted (with the server's message) by the global axios
// interceptor - toasting again here doubled every business-rule denial.
// All of this page's toasts sit bottom-left - top-right lands on the
// slide-over's header and actions.
// Quick confirmations, not announcements (app default is 5s).
// !rounded-xl beats toastify's own border-radius.
const TOAST_POS = {
  position: 'bottom-left',
  autoClose: 1500,
  className: '!rounded-xl',
  // The default countdown pauses while the window is unfocused (e.g.
  // DevTools) and on hover - these are quick confirmations, just go.
  pauseOnFocusLoss: false,
  pauseOnHover: false,
};
export const notify = {
  success: (msg) => toast.success(msg, TOAST_POS),
  error: (msg) => toast.error(msg, TOAST_POS),
};

export const toastApiError = (err, fallback) => {
  if (err?.response?.status === 403) return;
  notify.error(err?.response?.data?.msg || fallback);
};

const RATING_OPTIONS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export const Chip = ({ tone = 'slate', children, title }) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
      tone === 'green' && 'bg-emerald-50 dark:!bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
      tone === 'amber' && 'bg-amber-50 dark:!bg-amber-500/15 text-amber-700 dark:text-amber-300',
      tone === 'red' && 'bg-rose-50 dark:!bg-rose-500/15 text-rose-700 dark:text-rose-300',
      tone === 'blue' && 'bg-blue-50 dark:!bg-blue-500/15 text-blue-700 dark:text-blue-300',
      tone === 'slate' && 'bg-slate-100 dark:!bg-white/10 text-slate-600 dark:text-slate-300',
    )}
  >
    {children}
  </span>
);

// A legacy DocuSeal SA still signable by the veteran. 'superseded' = staff
// voided it from this page (archived on DocuSeal's side).
export const docusealInFlight = (row) =>
  !!row.docusealStatus && !['completed', 'superseded'].includes(row.docusealStatus);

// One label for the agreement journey, in-house first, legacy DocuSeal as a
// transition fallback, failed sends loudest.
export const saChipOf = (row) => {
  if (row.lastSaSendAction === 'sa:send:failed') {
    return { tone: 'red', label: 'Send failed' };
  }
  if (row.saSessionStatus === 'completed') return { tone: 'green', label: 'Signed' };
  if (row.saSessionStatus === 'sent' || row.saSessionStatus === 'viewed') {
    // An open session for a finished CLIENT is a post-sign correction; the
    // standing signed agreement remains in force until this one is signed.
    if (row.userType === 'client') {
      return { tone: 'amber', label: 'Correction awaiting signature' };
    }
    return { tone: 'blue', label: 'Awaiting signature' };
  }
  if (row.saSessionStatus === 'voided') {
    // A client's voided session was a correction attempt - the standing
    // signed agreement is still the truth.
    if (row.userType === 'client') return { tone: 'green', label: 'Signed' };
    return { tone: 'slate', label: 'Voided' };
  }
  if (row.docusealStatus === 'completed') return { tone: 'green', label: 'Signed (DocuSeal)' };
  if (docusealInFlight(row)) return { tone: 'blue', label: 'DocuSeal in flight' };
  if (row.docusealStatus === 'superseded') return { tone: 'slate', label: 'Voided' };
  return { tone: 'slate', label: 'Not sent' };
};

export const factsChipOf = (row) =>
  row.userIntakeConfirmedAt
    ? { tone: 'green', label: 'Confirmed' }
    : { tone: 'amber', label: 'Unconfirmed' };

// Custom rating dropdown: the native <select>'s open list is browser-drawn
// (sharp corners, OS blue) and can't be styled to match the card language.
export const RatingSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const label = value == null ? 'Select…' : `${value}%`;
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-8 w-24 items-center justify-between rounded-lg bg-white px-2.5 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10 dark:hover:!bg-white/10"
      >
        <span className={value == null ? 'text-slate-400' : 'font-medium'}>{label}</span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>
      {/* Two columns (0-50 | 60-100) halve the list's height; no internal
          scroll, so the panel's own scrollbar is the only one. right-0
          anchors the wider menu to the field's right edge, growing leftward
          so it stays inside the card. */}
      {open ? (
        <div className="absolute right-0 z-10 mt-1 w-36 rounded-xl border border-slate-100 dark:border-white/10 bg-white dark:!bg-surface-elevated py-1 shadow-lg">
          {/* 0% spans the full width so the columns align 10<->60 etc. */}
          <button
            type="button"
            onClick={() => {
              onChange(0);
              setOpen(false);
            }}
            className={cn(
              'block w-full border-b border-slate-100 dark:border-white/10 px-3 py-1 text-center text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:!bg-white/10 whitespace-nowrap',
              value === 0 && 'bg-slate-50 dark:!bg-white/5 font-semibold text-slate-900 dark:text-slate-50',
            )}
          >
            0%
          </button>
          <div className="flex">
            {[RATING_OPTIONS.slice(1, 6), RATING_OPTIONS.slice(6)].map((col, ci) => (
              <div key={ci} className={cn('flex-1', ci === 1 && 'border-l border-slate-100 dark:border-white/10')}>
                {col.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      onChange(r);
                      setOpen(false);
                    }}
                    className={cn(
                      'block w-full px-3 py-1 text-center text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:!bg-white/10 whitespace-nowrap',
                      value === r && 'bg-slate-50 dark:!bg-white/5 font-semibold text-slate-900 dark:text-slate-50',
                    )}
                  >
                    {`${r}%`}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const boolLabel = (v) => (v == null ? '-' : v ? 'Yes' : 'No');
const numLabel = (v) => (v == null ? '-' : String(v));
export const ratingLabel = (v) => (v == null ? '-' : `${v}%`);

// One fact on one line (Nic 2026-10-12): the mark, the fact, what they said
// (chips), then the answer on the right. Wraps on narrow screens.
// A small (i) with a short explanation on hover or focus (Nic 2026-10-23:
// SMC-K must never be a tripping point).
const InfoTip = ({ text, label }) => (
  <span className="group relative inline-flex shrink-0">
    <button
      type="button"
      aria-label={label || 'What is this?'}
      className="flex h-[16px] w-[16px] items-center justify-center rounded-full text-slate-400 outline-none hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-500 dark:hover:text-slate-300"
    >
      <Info size={12} />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-20 mb-[6px] hidden w-[250px] whitespace-normal rounded-lg bg-slate-900 px-[10px] py-[8px] text-[11.5px] font-normal leading-snug text-white shadow-lg group-focus-within:block group-hover:block dark:!bg-slate-700"
    >
      {text}
    </span>
  </span>
);

// hint: a short (i) beside the fact's name (SMC-K, Nic 2026-10-23).
const FactRow = ({ label, hint = null, done, missing, optional = false, chips = null, note = null, children }) => (
  // Narrow screens (Nic 2026-10-23): the chips start their own line under the
  // fact's name, left aligned, and the answer stays on the right; wider
  // screens keep them inline.
  <li className="rounded-lg bg-slate-50 px-3 py-2 dark:!bg-white/[0.04]">
    <div className="flex items-center gap-x-2.5">
      <span
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center self-start rounded-full mt-[1px] sm:mt-0 sm:self-center',
          done
            ? 'bg-emerald-500 text-white'
            : missing
              ? 'bg-amber-100 text-amber-600 dark:!bg-amber-500/20 dark:text-amber-300'
              : 'bg-slate-200/70 dark:!bg-white/10',
        )}
      >
        {done ? (
          <Check size={11} strokeWidth={3} />
        ) : missing ? (
          <span className="text-[10px] font-bold">!</span>
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-slate-500" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1.5">
      <span className="inline-flex items-center gap-[4px] text-[13px] font-semibold text-slate-800 dark:text-slate-100">
        {label}
        {hint ? <InfoTip text={hint} label={`What is ${label}?`} /> : null}
      </span>
      {missing ? (
        <span className="rounded-full bg-amber-100 px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-amber-700 dark:!bg-amber-500/20 dark:text-amber-300">
          Required
        </span>
      ) : optional ? (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Optional</span>
      ) : null}
      {chips ? (
        <span className="flex basis-full flex-col items-start gap-y-1.5 sm:basis-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2.5">
          {chips}
        </span>
      ) : null}
      </div>
      <span className={cn('ml-auto shrink-0 rounded-lg', missing && 'ring-2 ring-amber-300/80 dark:ring-amber-500/40')}>{children}</span>
    </div>
    {note ? <div className="mt-1 pl-[28px] text-[11.5px] text-slate-500 dark:text-slate-400">{note}</div> : null}
  </li>
);

// The facts list + confirm.
// plain: no card chrome, for hosts that already frame it (the call workspace).
// vaGovLead: { leadId, leadVaGovAccount, leadVaGovConfirmed } on a Sales
// signup: VA.gov joins the facts as a required one (Nic 2026-10-12).
// onboarding (Nic 2026-10-23): shown on the Onboarding side (the Workspace's
// Manage tab, Signed SAs). The client already signed and only sees their
// dashboard, so Sales' "Waiting for them to fill in their details" never
// shows there.
export const FactsSection = ({ api, row, claimedByMe, onConfirmed, onDirtyChange, plain = false, vaGovLead = null, onboarding = false }) => {
  const [vaAnswer, setVaAnswer] = useState(vaGovLead?.leadVaGovConfirmed || null);
  useEffect(() => setVaAnswer(vaGovLead?.leadVaGovConfirmed || null), [vaGovLead?.leadVaGovConfirmed]);
  const self = row.userSelfReport || null;
  // The Confirm service agreement check's finding (Onboarding's getAgreement,
  // Nic 2026-10-23): offered like their own answers, as "What VA pays". It
  // is the ONLY machine answer here (Nic 2026-10-29): Sales never sees a bot
  // chip, so anything without that source is ignored.
  const robot = row.robotSuggestion?.source === 'va_check' ? row.robotSuggestion : null;
  const vaCheck = !!robot;
  // The veteran by name ("Eric said 30%").
  const first = String(row.userFirstName || '').trim() || 'They';
  const firstPoss = first === 'They' ? 'Their' : `${first}'s`;
  // Facts LOCK once an agreement is out or signed: changing them mid-flight
  // silently invalidated the document the veteran was reading. Voiding the
  // open agreement (Agreement card) is the explicit unlock; a signed
  // agreement's facts never reopen here.
  const agreementOut =
    row.saSessionStatus === 'sent' ||
    row.saSessionStatus === 'viewed' ||
    // A signable legacy DocuSeal SA locks the facts exactly like an
    // in-house one - Void is the only way out for both.
    docusealInFlight(row);
  const agreementSigned =
    row.saSessionStatus === 'completed' || row.docusealStatus === 'completed';
  // Post-sign correction: the EXPLICIT marker (set by Correct & resend)
  // reopens the facts for a signed client until the corrected agreement
  // goes out. The confirm stamp can't carry this - legacy signers never
  // had one, and stamp-null must not read as correcting.
  const correcting =
    row.userType === 'client' && !!row.userIntakeCorrectionStartedAt && !agreementOut;
  const locked = (agreementOut || agreementSigned || row.userType === 'client') && !correcting;
  // A correction starts from what they signed, even for a client Sales never
  // confirmed (legacy signers): otherwise the form opened blank.
  const seeded = !!row.userIntakeConfirmedAt || correcting;
  const [rating, setRating] = useState(
    seeded ? Number(row.userCurrentVaRating ?? 0) : null,
  );
  const [married, setMarried] = useState(
    seeded ? !!row.userMarried : null,
  );
  const [dep18, setDep18] = useState(
    seeded ? Number(row.userDependentsUnder18 || 0) : 0,
  );
  const [depSchool, setDepSchool] = useState(
    seeded ? Number(row.userDependents18To24InSchool || 0) : 0,
  );
  const [smcK, setSmcK] = useState(
    seeded ? Number(row.userSmcKCount || 0) : 0,
  );
  const [saving, setSaving] = useState(false);

  const applyColumn = (src) => {
    if (!src) return;
    setRating(Number(src.rating ?? 0));
    setMarried(!!(src.married ?? src.isMarried));
    setDep18(Number(src.dependentsUnder18 ?? 0));
    setDepSchool(Number(src.dependents18To24InSchool ?? 0));
    setSmcK(Number(src.smcKCount ?? 0));
  };

  // Edited-but-not-reconfirmed facts must disarm the Send button: a send
  // always uses the STORED confirmed values, never the form's draft.
  const dirty =
    !!row.userIntakeConfirmedAt &&
    (rating !== Number(row.userCurrentVaRating ?? 0) ||
      married !== !!row.userMarried ||
      dep18 !== Number(row.userDependentsUnder18 || 0) ||
      depSchool !== Number(row.userDependents18To24InSchool || 0) ||
      smcK !== Number(row.userSmcKCount || 0));
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const confirm = async () => {
    if (rating == null || married == null) {
      notify.error('Set a rating and marital status before confirming.');
      return;
    }
    if (vaGovLead && !vaAnswer) {
      notify.error('Ask about their VA.gov account before confirming.');
      return;
    }
    setSaving(true);
    try {
      await api.confirmFacts({
        rating,
        married,
        dependentsUnder18: dep18,
        dependents18To24InSchool: depSchool,
        smcKCount: smcK,
      });
      notify.success('Facts confirmed');
      onConfirmed();
    } catch (err) {
      toastApiError(err, 'Could not confirm facts.');
    } finally {
      setSaving(false);
    }
  };

  // One config row per fact: raw values from each source + how to format
  // them + how a click applies that single value into the Confirmed column.
  const factsCfg = [
    {
      label: 'VA rating',
      selfVal: self?.rating ?? null,
      robotVal: robot?.rating ?? null,
      current: rating,
      fmt: ratingLabel,
      apply: (v) => setRating(Number(v)),
    },
    {
      label: 'Married',
      selfVal: self?.married ?? null,
      robotVal: robot?.isMarried ?? null,
      current: married,
      fmt: boolLabel,
      apply: (v) => setMarried(!!v),
    },
    {
      label: 'Dependents under 18',
      selfVal: self?.dependentsUnder18 ?? null,
      robotVal: robot?.dependentsUnder18 ?? null,
      current: dep18,
      fmt: numLabel,
      apply: (v) => setDep18(Number(v)),
    },
    {
      label: '18-24 in school',
      selfVal: self?.dependents18To24InSchool ?? null,
      robotVal: robot?.dependents18To24InSchool ?? null,
      current: depSchool,
      fmt: numLabel,
      apply: (v) => setDepSchool(Number(v)),
    },
    {
      label: 'SMC-K count',
      hint: "SMC-K: extra VA pay (about $140 a month each) for losing the use of a hand, foot, eye, or a reproductive organ (often ED). It's only here so the math matches the VA's payment: it doesn't change their agreement's fee. Not sure? Use what the VA pays.",
      selfVal: null,
      // The wizard never asks the veteran about SMC-K (most don't know the
      // term), so there is no "<name> said" chip for it (Nic 2026-10-05).
      noSelf: true,
      robotVal: robot?.smcKCount ?? null,
      current: smcK,
      fmt: numLabel,
      apply: (v) => setSmcK(Number(v)),
    },
  ];

  // Premium facts form (Nic 2026-09-23): one tile per fact, and the rep's
  // answer is the hero. The two sources are small chips that fill the
  // answer with a click; the answer lives in its own highlighted panel.
  const answerSet = [rating != null, married != null];
  const requiredTotal = vaGovLead ? 3 : 2;
  const requiredDone = answerSet.filter(Boolean).length + (vaGovLead && vaAnswer ? 1 : 0);

  const Stepper = ({ value, setter, max }) => (
    <div className="inline-flex h-8 items-center overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10">
      <button
        type="button"
        onClick={() => setter(Math.max(0, value - 1))}
        disabled={value <= 0}
        className="flex h-8 w-8 items-center justify-center text-base font-semibold text-slate-500 outline-none hover:bg-slate-50 focus:outline-none disabled:opacity-30 dark:text-slate-300 dark:hover:!bg-white/10"
      >
        -
      </button>
      <span className="w-8 text-center text-sm font-bold tabular-nums text-slate-800 dark:text-slate-100">
        {value}
      </span>
      <button
        type="button"
        onClick={() => setter(Math.min(max, value + 1))}
        disabled={value >= max}
        className="flex h-8 w-8 items-center justify-center text-base font-semibold text-slate-500 outline-none hover:bg-slate-50 focus:outline-none disabled:opacity-30 dark:text-slate-300 dark:hover:!bg-white/10"
      >
        +
      </button>
    </div>
  );

  // A source chip: "Eric said 40%". Click to use it as the answer; once the
  // answer matches, it wears a check. No color fade (a fading chip read as
  // the old state flashing back).
  const SourceChip = ({ tone, icon: Icon, label, value, fmt, apply, emptyLabel, current }) => {
    const empty = value == null;
    const matches =
      !empty && current != null && (typeof value === 'boolean' ? !!current === value : Number(current) === Number(value));
    return (
      <button
        type="button"
        disabled={empty}
        onClick={() => apply(value)}
        title={empty ? undefined : matches ? 'Your answer matches' : 'Use this as your answer'}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium outline-none focus:outline-none disabled:cursor-default whitespace-nowrap',
          empty
            ? tone === 'sky'
              ? 'bg-sky-50/70 text-sky-700/60 ring-1 ring-sky-200/70 dark:!bg-sky-500/[0.07] dark:text-sky-300/60 dark:ring-sky-500/20'
              : 'bg-violet-50/70 text-violet-700/60 ring-1 ring-violet-200/70 dark:!bg-violet-500/[0.07] dark:text-violet-300/60 dark:ring-violet-500/20'
            : tone === 'sky'
              ? 'bg-sky-50 text-sky-800 ring-1 ring-sky-300 hover:bg-sky-100 dark:!bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/40'
              : 'bg-violet-50 text-violet-800 ring-1 ring-violet-200 hover:bg-violet-100 dark:!bg-violet-500/15 dark:text-violet-200 dark:ring-violet-500/30',
        )}
      >
        {matches ? <Check size={12} strokeWidth={3} /> : <Icon size={12} />}
        {label}
        <span className="font-bold">{empty ? emptyLabel || '-' : fmt(value)}</span>
      </button>
    );
  };

  const answerFor = (i) =>
    i === 0 ? (
      <RatingSelect value={rating} onChange={setRating} />
    ) : i === 1 ? (
      <div className="inline-flex h-8 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => setMarried(v)}
            className={cn(
              'w-14 text-[13px] font-semibold outline-none transition-[background-color,color] focus:outline-none',
              married === v
                ? 'bg-emerald-600 text-white'
                : 'text-slate-500 hover:bg-slate-50 dark:text-slate-300 dark:hover:!bg-white/10',
            )}
          >
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    ) : i === 2 ? (
      <Stepper value={dep18} setter={setDep18} max={20} />
    ) : i === 3 ? (
      <Stepper value={depSchool} setter={setDepSchool} max={20} />
    ) : (
      // The VA awards at most 3 concurrent SMC-K's.
      <Stepper value={smcK} setter={setSmcK} max={3} />
    );

  return (
    <div className={plain ? '' : cn(CARD, 'dark:!bg-surface-card dark:ring-1 dark:ring-white/10', 'p-5')}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">
            Confirm their facts
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Ask each one on the call. Their agreement uses exactly what you confirm here.
          </div>
        </div>
        {row.userIntakeConfirmedAt ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300">
            <CheckCircle2 size={14} />
            Confirmed {agoLabel(row.userIntakeConfirmedAt)}
            {row.confirmedByName?.trim() ? ` by ${row.confirmedByName.trim()}` : ''}
          </span>
        ) : null}
      </div>

      {/* What they filled in themselves (Nic 2026-09-30: it was easy to
          miss). Filled in: say so, and one button uses it all. Not yet:
          say we are waiting, so an empty chip never reads as missing. */}
      {/* Not while the facts are locked: nothing here can be used then. */}
      {/* What VA pays (Nic 2026-10-23): the VA check's finding, offered the
          same way as their own answers, whenever the facts are open. */}
      {vaCheck && !locked ? (
        <div className="mb-3 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-xl px-3 py-2.5 sm:flex sm:flex-wrap sm:gap-3 sm:py-2 bg-violet-50 ring-1 ring-violet-200 dark:!bg-violet-500/10 dark:ring-violet-500/30">
          <span className="row-span-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 sm:row-span-1 sm:h-7 sm:w-7 dark:!bg-violet-500/20 dark:text-violet-200">
            <Landmark size={14} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-violet-900 dark:text-violet-100">The VA check found what VA pays</div>
            <div className="hidden text-xs text-violet-800/80 sm:block dark:text-violet-200/80">
              It&apos;s next to each fact below. Use it, then check each one with them on the call.
            </div>
          </div>
          <button
            type="button"
            disabled={!claimedByMe || locked}
            onClick={() => applyColumn(robot)}
            className="col-start-2 inline-flex h-8 w-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-violet-600 px-3 text-xs font-semibold text-white shadow-sm outline-none hover:bg-violet-500 focus:outline-none disabled:opacity-40 sm:w-auto"
          >
            <Check size={14} /> Use what VA pays
          </button>
        </div>
      ) : null}

      {/* Sales: as before. Onboarding: their own answers only when they gave
          some, never the waiting line. */}
      {!locked && (onboarding ? !!self && (!row.userIntakeConfirmedAt || correcting) : !row.userIntakeConfirmedAt) ? (
        self ? (
          <div className="mb-3 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-xl px-3 py-2.5 sm:flex sm:flex-wrap sm:gap-3 sm:py-2 bg-sky-50 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:ring-sky-500/30">
            <span className="row-span-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 sm:row-span-1 sm:h-7 sm:w-7 dark:!bg-sky-500/20 dark:text-sky-200">
              <UserRound size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-sky-900 dark:text-sky-100">
                {first} filled in their details
              </div>
              <div className="hidden text-xs text-sky-800/80 sm:block dark:text-sky-200/80">
                Their answers are next to each fact below. Use them, then check each one with them on the call.
              </div>
            </div>
            <button
              type="button"
              disabled={!claimedByMe || locked}
              onClick={() => applyColumn(self)}
              className="col-start-2 inline-flex h-8 w-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-sky-600 px-3 text-xs font-semibold text-white shadow-sm outline-none hover:bg-sky-500 focus:outline-none disabled:opacity-40 sm:w-auto"
            >
              <Check size={14} /> Use all of {firstPoss} answers
            </button>
          </div>
        ) : (
          <div className="mb-3 flex items-center gap-3 rounded-xl bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/30">
            <Loader2 size={15} className="shrink-0 animate-spin text-sky-500 dark:text-sky-300" />
            <span>
              Waiting for {first === 'They' ? 'them' : first} to fill in their details. Their answers show up here once they do, or ask each one on the call.
            </span>
          </div>
        )
      ) : null}

      {correcting ? (
        <div className="mb-3 rounded-lg bg-amber-50 dark:!bg-amber-500/15 px-3 py-2 text-xs font-medium text-amber-800 dark:text-amber-200">
          Correcting facts for a signed agreement. Edit and re-confirm, then
          send the corrected agreement. The signed agreement stays in force
          until the new one is signed. Cancel below to leave it as is.
        </div>
      ) : null}

      {/* Same progression model as the Agreement card below: the whole form
          is inert until the viewer has claimed the client, and locks again
          once an agreement is out. */}
      <div className="relative">
        {locked ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white dark:!bg-white/5 shadow-md ring-1 ring-slate-200 dark:ring-white/10">
              <Lock size={16} className="text-slate-500 dark:text-slate-400" />
            </span>
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              {agreementSigned || (row.userType === 'client' && !agreementOut)
                ? 'Facts are locked. The agreement is signed.'
                : 'Facts are locked while an agreement is out.'}
            </div>
            {!agreementSigned && !(row.userType === 'client' && !agreementOut) ? (
              <div className="text-[11px] text-slate-400">
                Void the agreement below to make changes.
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={cn(
            (!claimedByMe || locked) && 'pointer-events-none select-none',
            locked ? 'opacity-30' : !claimedByMe && 'opacity-40',
          )}
        >
          <ul className="mb-0 list-none space-y-1.5 pl-0">
            {factsCfg.map((f, i) => {
              // Plain states (Nic 2026-09-30: green around every answer read
              // as "done" next to the amber "!"). Green means ONE thing: a
              // required answer is in. Missing required = amber, and its
              // answer box gets the amber outline. Optional counts start at
              // 0, so on their own they never show as done; an optional one
              // turns green once the answer matches what the client said
              // (Nic 2026-10-05: it is confirmed, not just left alone).
              const required = i < 2;
              const matchesClient =
                !f.noSelf &&
                f.selfVal != null &&
                f.current != null &&
                Number(f.current) === Number(f.selfVal);
              const done = required ? answerSet[i] : matchesClient;
              const missing = required && !answerSet[i];
              // One line per fact (Nic 2026-10-12: it stretched too far down):
              // the mark, the fact, what they said, then the answer.
              return (
                <FactRow
                  key={f.label}
                  label={f.label}
                  hint={f.hint || null}
                  done={done}
                  missing={missing}
                  optional={!required}
                  chips={
                    !f.noSelf || robot ? (
                      <>
                        {f.noSelf ? null : (
                          <SourceChip
                            tone="sky"
                            icon={UserRound}
                            label={`${first} said`}
                            value={f.selfVal}
                            current={f.current}
                            fmt={f.fmt}
                            apply={f.apply}
                          />
                        )}
                        {robot ? (
                          <SourceChip
                            tone="violet"
                            icon={Landmark}
                            label="VA pays"
                            value={f.robotVal}
                            current={f.current}
                            fmt={f.fmt}
                            apply={f.apply}
                          />
                        ) : null}
                      </>
                    ) : null
                  }
                >
                  {answerFor(i)}
                </FactRow>
              );
            })}
            {/* VA.gov, one of the facts (Nic 2026-10-12): required, so
                Confirm facts is the last thing pressed. It saves on its own
                the moment it's picked. */}
            {vaGovLead ? (
              <FactRow
                label="VA.gov account"
                done={!!vaAnswer}
                missing={!vaAnswer}
                chips={
                  vaGovLead.leadVaGovAccount ? (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-50 px-2.5 py-0.5 text-[12px] font-medium text-sky-800 ring-1 ring-sky-300 dark:!bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/40">
                      <UserRound size={12} /> {first} said
                      <span className="font-bold">{VA_GOV_LABEL[vaGovLead.leadVaGovAccount] || vaGovLead.leadVaGovAccount}</span>
                    </span>
                  ) : null
                }
                note={
                  vaAnswer === 'yes'
                    ? 'They land in onboarding ready to work.'
                    : vaAnswer
                      ? 'Not a stop. Setting up VA.gov is their first step; their portal walks them through it.'
                      : null
                }
              >
                <VaGovCheck inline lead={vaGovLead} onChange={setVaAnswer} />
              </FactRow>
            ) : null}
          </ul>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {requiredDone === requiredTotal
                ? 'All set. Confirm to lock these in.'
                : `${requiredDone} of ${requiredTotal} required answered`}
            </span>
            <button
              type="button"
              disabled={saving || !claimedByMe || locked || requiredDone < requiredTotal}
              onClick={confirm}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm outline-none transition-[background-color] hover:bg-emerald-500 focus:outline-none disabled:opacity-40"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {row.userIntakeConfirmedAt ? 'Re-confirm facts' : 'Confirm facts'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// sendBlocked: a reason the first agreement can't go out yet (Sales: the
// VA.gov question, Nic 2026-10-10). The server refuses it too.
// signedPdf (Signed SAs, Nic 2026-10-23): their signed PDF { claimId, fileId }
// opens in a new tab beside "The signed agreement is on file"; null shows it
// greyed (no file on a claim); left out, no button (the Workspace).
export const AgreementSection = ({
  api,
  row,
  claimedByMe,
  factsDirty,
  onChanged,
  plain = false,
  sendBlocked = null,
  signedPdf,
  onOpenSignedPdf = null,
  // { ask: 'correct', n } (Nic 2026-10-23): opened from the VA check's
  // Correct & resend, the card lands with the same "Start a correction?"
  // confirm a click on Correct & Resend shows here. n re-fires a repeat.
  autoAsk = null,
  onAutoAsked = null,
}) => {
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const chip = saChipOf(row);
  const confirmed = !!row.userIntakeConfirmedAt;
  const legacyOut = docusealInFlight(row);
  const isClient = row.userType === 'client';
  const sessionOut = row.saSessionStatus === 'sent' || row.saSessionStatus === 'viewed';
  // Post-sign correction lane: the explicit marker (set by Correct &
  // resend) - works for in-house AND legacy DocuSeal signers alike.
  const correcting = isClient && !!row.userIntakeCorrectionStartedAt && !sessionOut;
  // Correction freshness: the confirmation must be NEWER than the marker
  // (the stamp survives Correct & resend so Cancel restores everything).
  const freshlyConfirmed =
    !row.userIntakeCorrectionStartedAt ||
    (confirmed &&
      new Date(row.userIntakeConfirmedAt) > new Date(row.userIntakeCorrectionStartedAt));
  // Send stays locked while the legacy DocuSeal SA is signable - voiding it
  // (archive on DocuSeal's side) is the required first step.
  const unlocked = confirmed && freshlyConfirmed && claimedByMe && !factsDirty && !legacyOut && !(sendBlocked && !isClient);
  const failedBody = row.lastSaSendAction === 'sa:send:failed' ? row.lastSaSendBody : null;
  const [beginning, setBeginning] = useState(false);
  // An in-card "are you sure" (Nic 2026-10-06: never the browser's pop-up).
  // 'void' | 'correct' | null.
  const [ask, setAsk] = useState(null);
  // Why this correction (Nic 2026-10-26): required, reporting only. From the
  // VA check's Correct & resend it starts on "The VA check found a difference".
  const [why, setWhy] = useState({ reason: '', note: '' });
  const askCorrection = (reason = '') => {
    setWhy({ reason, note: '' });
    setAsk('correct');
  };
  const whyReady = !!why.reason && (why.reason !== 'other' || !!why.note.trim());
  const canStartCorrection = isClient && !sessionOut && !correcting && claimedByMe;
  // Once per click (n): Cancel correction makes a correction startable again,
  // and must land on the plain card (as if Keep it), not re-ask.
  const askedFor = useRef(null);
  useEffect(() => {
    if (autoAsk?.ask !== 'correct' || !canStartCorrection || askedFor.current === autoAsk.n) return;
    askedFor.current = autoAsk.n;
    askCorrection('va_check');
    if (onAutoAsked) onAutoAsked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAsk?.ask, autoAsk?.n, canStartCorrection]);

  const beginCorrection = async () => {
    setAsk(null);
    setBeginning(true);
    try {
      await api.beginCorrection({ reason: why.reason, note: why.note.trim() || null });
      notify.success('Correction started - facts unlocked');
      onChanged();
    } catch (err) {
      toastApiError(err, 'Could not start the correction.');
    } finally {
      setBeginning(false);
    }
  };

  const cancelCorrection = async () => {
    setBeginning(true);
    try {
      await api.cancelCorrection();
      notify.success('Correction cancelled - the signed agreement stands');
      onChanged();
    } catch (err) {
      toastApiError(err, 'Could not cancel the correction.');
    } finally {
      setBeginning(false);
    }
  };

  const voidMsg = isClient
    ? "Their signing link stops working. The agreement they already signed stays in force."
    : legacyOut
      ? "The DocuSeal link they received stops working. You'll re-confirm their facts before sending the new agreement."
      : "The signing link they received stops working. You'll re-confirm their facts before sending it again.";
  const doVoid = async () => {
    setAsk(null);
    setSending(true);
    try {
      await api.voidAgreement();
      notify.success('Agreement voided');
      onChanged();
    } catch (err) {
      toastApiError(err, 'Could not void the agreement.');
    } finally {
      setSending(false);
    }
  };

  // Tokens are stored hashed, so the open session's link can't be re-read -
  // this REISSUES it (latest link wins; the previously texted one rotates
  // out, same as Re-send).
  const copyLink = async () => {
    setCopying(true);
    try {
      const res = await api.signLink();
      const body = res.body || res;
      if (body.signUrl) {
        await navigator.clipboard?.writeText(body.signUrl);
        notify.success('Signing link copied');
      }
    } catch (err) {
      toastApiError(err, 'Could not get the signing link.');
    } finally {
      setCopying(false);
    }
  };

  const send = async (regenerate = false) => {
    setSending(true);
    try {
      const res = await api.sendAgreement({ regenerate });
      const body = res.body || res;
      notify.success(
        body.reused ? 'Signing link re-sent' : 'Service Agreement sent',
      );
      onChanged();
    } catch (err) {
      toastApiError(err, 'Could not send the Service Agreement.');
      onChanged(); // refresh so the recorded failure surfaces
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={plain ? 'rounded-xl bg-slate-50 p-4 dark:!bg-white/[0.04]' : cn(CARD, 'dark:!bg-surface-card dark:ring-1 dark:ring-white/10', 'p-4')}>
      <div className="mb-2 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200">
          <FileSignature size={15} className="text-slate-400" />
          Service Agreement
        </div>
        <Chip tone={chip.tone}>{chip.label}</Chip>
      </div>

      {failedBody ? (
        <div className="mb-2 rounded-lg bg-rose-50 dark:!bg-rose-500/15 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
          Last send failed{failedBody.message ? `: ${failedBody.message}` : ''}. Fix the
          issue and retry.
        </div>
      ) : null}

      {factsDirty ? (
        <div className="mb-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
          The facts above were edited. Re-confirm them before sending.
        </div>
      ) : null}

      {/* Signed = steady state. Without this guard the Send button survives
          the completion (the confirm stamp is still set), and clicking it
          would mint a NEW session and text the client a fresh signing link
          for an agreement they already signed. Corrections leave this state
          through the explicit Correct & resend step. */}
      {(/^Signed/.test(chip.label) || (isClient && !sessionOut)) && !correcting ? (
        <div className={signedPdf !== undefined ? 'space-y-2.5' : 'flex flex-wrap items-center gap-3'}>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            The signed agreement is on file.
          </div>
          <div className={signedPdf !== undefined ? cn('grid gap-2', isClient && ask !== 'correct' ? 'grid-cols-2' : 'grid-cols-1') : 'contents'}>
          {signedPdf !== undefined ? (
            <button
              type="button"
              disabled={!signedPdf}
              onClick={() => signedPdf && onOpenSignedPdf?.(signedPdf)}
              title={signedPdf ? 'Open the signed agreement in a new tab' : 'No signed PDF is on file for this agreement.'}
              // Even halves at every size (Nic 2026-10-23): each button shrinks
              // inside its half, never spilling out of it.
              className="inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-2 py-1.5 text-[13px] font-semibold text-slate-700 hover:border-slate-900 hover:!bg-slate-900 hover:text-white disabled:pointer-events-none disabled:opacity-50 sm:px-3 sm:text-sm dark:border-white/15 dark:text-slate-200 dark:hover:!bg-slate-100 dark:hover:text-slate-900"
            >
              <ExternalLink size={14} className="shrink-0" />
              <span className="truncate">View PDF</span>
            </button>
          ) : null}
          {/* Corrections are a CLIENT verb (any provider - in-house or
              legacy DocuSeal); a pending row's signed state promotes
              instead. */}
          {/* Hidden while its own Start a correction? shows (Nic 2026-10-23):
              the confirm is the next step, not a second button to click. */}
          {isClient && ask !== 'correct' ? (
            <button
              type="button"
              // Greyed when they can't (Nic 2026-10-23): someone off the team,
              // or a claim past With a CM (the page says why).
              disabled={beginning || !claimedByMe}
              onClick={() => askCorrection()}
              title={
                claimedByMe
                  ? 'Reopen the facts and send a corrected agreement. The signed one stays in force until the new one is signed.'
                  : "This agreement can't be corrected here."
              }
              className="inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-200 px-2 py-1.5 text-[13px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50 sm:px-3 sm:text-sm dark:border-amber-500/40 dark:text-amber-300 dark:hover:!bg-amber-500/15"
            >
              <FileSignature size={14} className="shrink-0" />
              {/* Phone width: just "Correct", so the half never cuts it off. */}
              <span className="truncate">
                <span className="sm:hidden">Correct</span>
                <span className="hidden sm:inline">Correct &amp; Resend</span>
              </span>
            </button>
          ) : null}
          </div>
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-2">
        {/* The send takes a few seconds server-side (PDF prep + envelope +
            notify) - a live spinner beats a silently greyed button. */}
        <button
          type="button"
          disabled={!unlocked || sending}
          onClick={() => send(false)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-surface-page px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-surface-page/90 disabled:opacity-40 whitespace-nowrap dark:!bg-slate-100 dark:!text-slate-900 dark:hover:!bg-white"
        >
          {sending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <FileSignature size={15} />
          )}
          {sending
            ? 'Sending…'
            : sessionOut
              ? 'Re-send link'
              : isClient
                ? 'Send corrected agreement'
                : 'Send Service Agreement'}
        </button>
        {sendBlocked && !isClient && !sessionOut ? (
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">{sendBlocked}</span>
        ) : null}
        {correcting ? (
          <button
            type="button"
            disabled={beginning || sending}
            onClick={cancelCorrection}
            title="Abandon the correction - the signed agreement stays as it is"
            // A subtle red (Nic 2026-10-23): it backs out of the correction.
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-solid border-rose-200 bg-rose-50/60 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:border-rose-300 hover:bg-rose-100/70 disabled:opacity-50 dark:border-rose-500/30 dark:!bg-rose-500/10 dark:text-rose-300 dark:hover:!bg-rose-500/20"
          >
            <X size={14} />
            Cancel correction
          </button>
        ) : null}
        {/* The explicit flow is Void -> edit facts -> confirm -> Send; a
            standalone Regenerate button became redundant (the backend's
            regenerate path remains for the COLA cron). */}
        {(row.saSessionStatus === 'sent' || row.saSessionStatus === 'viewed' || legacyOut) && (
          <button
            type="button"
            disabled={!claimedByMe || sending}
            onClick={() => setAsk('void')}
            title="Kill the outstanding signing link and unlock the facts for changes"
            className="inline-flex items-center gap-1.5 rounded-lg border-[1px] border-rose-200 dark:border-rose-500/40 px-3 py-1.5 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:!bg-rose-500/15 disabled:opacity-50 whitespace-nowrap"
          >
            <X size={14} />
            Void agreement
          </button>
        )}
        {row.saSessionStatus === 'sent' || row.saSessionStatus === 'viewed' ? (
          <button
            type="button"
            disabled={!claimedByMe || copying}
            onClick={copyLink}
            title="Copy signing link"
            aria-label="Copy signing link"
            className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:!bg-white/10 hover:text-slate-700 disabled:opacity-50 whitespace-nowrap"
          >
            <Copy size={15} />
          </button>
        ) : null}
      </div>
      )}

      {ask ? (
        <ConfirmPanel
          tone={ask === 'void' ? 'rose' : 'amber'}
          title={
            ask === 'void'
              ? isClient
                ? 'Void the corrected agreement?'
                : 'Void this agreement?'
              : 'Start a correction?'
          }
          body={
            ask === 'void'
              ? voidMsg
              : 'Their facts unlock so you can send a corrected agreement. The one they signed stays in force until they sign the new one.'
          }
          confirmLabel={ask === 'void' ? 'Void agreement' : 'Start correction'}
          busy={sending || beginning}
          confirmDisabled={ask === 'correct' && !whyReady}
          onConfirm={ask === 'void' ? doVoid : beginCorrection}
          onCancel={() => setAsk(null)}
        >
          {ask === 'correct' ? <CorrectionReason why={why} onChange={setWhy} /> : null}
        </ConfirmPanel>
      ) : null}
    </div>
  );
};

// The in-app "are you sure" (Nic 2026-10-06): opens inside the card it acts
// on, says plainly what happens, and the safe choice is the quiet one.
// Escape cancels. Both themes.
// Why a correction (Nic 2026-10-26): kept for reporting only, never shown
// to anyone. The server checks the same list (clientIntakeController).
const CORRECTION_REASONS = [
  { value: 'va_check', label: 'The VA check found a difference' },
  { value: 'client_update', label: 'They told us something changed' },
  { value: 'our_mistake', label: 'We entered something wrong' },
  { value: 'other', label: 'Something else' },
];
// A dropdown and a note on one line (Nic 2026-10-26: saves space), stacking
// on a phone. The dropdown is drawn like RatingSelect above, never the
// browser's own list.
const FIELD =
  'h-9 rounded-lg bg-white px-2.5 text-[13px] text-slate-800 shadow-sm outline-none ring-1 ring-amber-200 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10';
const CorrectionReason = ({ why, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const picked = CORRECTION_REASONS.find((r) => r.value === why.reason);
  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
      <div className="relative sm:w-64 sm:shrink-0" ref={ref}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Why does it need a correction?"
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            // Close the list, not the whole panel.
            if (e.key === 'Escape' && open) {
              e.nativeEvent.stopPropagation();
              setOpen(false);
            }
          }}
          className={cn(FIELD, 'flex w-full items-center justify-between gap-2 text-left hover:bg-amber-50/40 focus-visible:ring-2 focus-visible:ring-amber-400')}
        >
          <span className={cn('truncate', !picked && 'text-slate-400')}>{picked ? picked.label : 'Why does it need a correction?'}</span>
          <ChevronDown size={14} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
        </button>
        {open ? (
          <div
            role="listbox"
            className="absolute left-0 right-0 z-20 mt-1 rounded-xl border border-slate-100 bg-white py-1 shadow-lg dark:border-white/10 dark:!bg-surface-elevated"
          >
            {CORRECTION_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                role="option"
                aria-selected={why.reason === r.value}
                onClick={() => {
                  onChange({ ...why, reason: r.value });
                  setOpen(false);
                }}
                className={cn(
                  'block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:!bg-white/10',
                  why.reason === r.value && 'bg-slate-50 font-semibold text-slate-900 dark:!bg-white/5 dark:text-slate-50',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <input
        type="text"
        value={why.note}
        maxLength={300}
        onChange={(e) => onChange({ ...why, note: e.target.value })}
        placeholder={why.reason === 'other' ? 'Say why in a few words' : 'A short note (optional)'}
        aria-label="Correction note"
        className={cn(FIELD, 'min-w-0 flex-1 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-400')}
      />
    </div>
  );
};

const ConfirmPanel = ({ tone, title, body, confirmLabel, busy, confirmDisabled = false, onConfirm, onCancel, children }) => {
  React.useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const rose = tone === 'rose';
  return (
    <div
      role="alertdialog"
      aria-label={title}
      className={cn(
        'mt-3 rounded-xl p-3.5 ring-1',
        rose
          ? 'bg-rose-50 ring-rose-200 dark:!bg-rose-500/10 dark:ring-rose-400/25'
          : 'bg-amber-50 ring-amber-200 dark:!bg-amber-500/10 dark:ring-amber-400/25',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white',
            rose ? 'bg-rose-600' : 'bg-amber-500',
          )}
        >
          <AlertTriangle size={15} strokeWidth={2.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className={cn('text-sm font-bold', rose ? 'text-rose-800 dark:text-rose-200' : 'text-amber-900 dark:text-amber-100')}>
            {title}
          </div>
          <div className={cn('mt-0.5 text-[13px] leading-relaxed', rose ? 'text-rose-700/90 dark:text-rose-200/80' : 'text-amber-800/90 dark:text-amber-100/80')}>
            {body}
          </div>
          {children}
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={busy}
              onClick={onCancel}
              className="rounded-lg bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 outline-none hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50 dark:!bg-white/10 dark:text-slate-100 dark:ring-white/10 dark:hover:!bg-white/15"
            >
              Keep it
            </button>
            <button
              type="button"
              disabled={busy || confirmDisabled}
              onClick={onConfirm}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm outline-none focus:outline-none disabled:opacity-50',
                rose ? 'bg-rose-600 hover:bg-rose-500' : 'bg-amber-600 hover:bg-amber-500',
              )}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
